import { Injectable, OnDestroy, OnInit } from "@angular/core";
import { FileItemService } from "./file-item.service";
import { canBrowserOpen, getFileExt } from "../shared/func";
import { LinkFileListService } from "../link-consume/services/link-file-list.service";
import { UserService } from "../core/user.service";
import { BlendEvent, FileItem, User, WebpathActionType } from "../shared/models";
import { ActivatedRoute, Router } from "@angular/router";
import { BuildTransferItemService } from "../transfer/services/build-transfer-item.service";
import { debounceTime, Subscription } from "rxjs";
import { IFile } from "src/sync";
import { MoveActionsService } from "../shared/services/move-actions.service";
import { IDuplicateNameMap } from "../shared/models/move.model";
import { Store } from "@ngrx/store";
import * as fromRoot from '../reducers';
import * as FileListActions from '../actions/file-list.actions';
import { SyncCryptService } from "../core/crypt/sync-crypt.service";
import { StarredListService } from "../file-list/services/starred-list.service";
import { SnackbarService } from "../shared/services/snackbar.service";
import { SkuService } from "../shared/services/sku.service";
import { FileListService } from "../file-list/services/file-list.service";
import { DialogService } from "../shared/services/dialog.service";
import { DialogUpgradeComponent } from "../file-list/dialog-upgrade/dialog-upgrade.component";
import { BlendService } from "../shared/services/blend.service";

@Injectable({
  providedIn: 'root',
})
export class FileViewService implements OnDestroy {
  private compat: number;
  private isUploadAllowed: number;
  private isFileEditAllowed: number;
  private isCwdShared: number;
  private isAuth: boolean;
  private user: User;
  public inited: Promise<void>;
  private linkPathListSubscription: Subscription;
  private listSubscription: Subscription;
  private isFreeUser: boolean;
  private total_favorite: number;

  constructor(
    private fileItemService: FileItemService,
    private LinkPathList: LinkFileListService,
    private userService: UserService,
    private router: Router,
    private buildTransferItemService: BuildTransferItemService,
    private moveActionsService: MoveActionsService,
    private syncCryptService: SyncCryptService,
    private starredListService: StarredListService,
    private fileListService: FileListService,
    private store: Store<fromRoot.State>,
    private snackbarService: SnackbarService,
    private skuService: SkuService,
    private dialogService: DialogService,
    private blendService: BlendService,
  ) {
    this.isAuth = this.userService.isAuthenticated();
    this.user = this.userService.getUser();

    this.inited = new Promise<void>(resolve => {
      let initCount = 3;
      let resolved = false;
      const init = () => {
        if (!resolved) {
          if (!--initCount) {
            resolve();
            resolved = true;
          }  
        }
      }

      this.skuService.isFreeUser().then(value => {
        this.isFreeUser = value;
        init();
      });
  
      this.listSubscription = this.fileListService.getListSubscription().pipe(debounceTime(500)).subscribe((data) => {
        if (data.loaded) {
          this.total_favorite = data.total_favorite;
          init();
        }
      });

      this.linkPathListSubscription = this.LinkPathList.getSubscription().subscribe(
        async (data) => {
          if (data.loaded && data.sorted) {
            this.isUploadAllowed = data.upload;
            this.compat = data.compat;
            this.isFileEditAllowed = data.file_edit;
            this.isCwdShared = data.cwd.is_shared;
          }
          init();
        }
      );
    })
  }

  ngOnDestroy() {
    this.linkPathListSubscription.unsubscribe();
  }

  public isMSOffice(item) {
    const app = this.fileItemService.getMSOfficeApp(item);
    if (app === 'None') {
      return false;
    }
    return true;
  }

  public isOpenable(item) {
    if (item.context == 'link' && (item.previewonly == 8 || !this.compat)) {
      return false;
    }
    if (
      item.context == 'link' &&
      this.fileItemService.isStreamable(item.kind)
    ) {
      return false;
    }
    if (
      item.context == 'link' &&
      getFileExt(item.name) == 'pdf' &&
      item.link_is_business
    ) {
      return false;
    }
    if (this.isMSOffice(item)) {
      return false;
    }

    if (!canBrowserOpen(item.name)) {
      return false;
    }
    // return this.item.compat
    //     ? canLinkBrowserOpen(this.item.name)
    //     : canBrowserOpen(this.item.name);
    return true;
  }

  public showOfficeEdit(item) {
    const inLink = item.context !== 'files';
    if (inLink) {
      return (
        this.compat && this.fileItemService.isOfficeLinkEdit(item, this.isUploadAllowed, this.isFileEditAllowed, this.isCwdShared)
      );
    }
    return this.fileItemService.isMSOffice(item, 'edit') && this.isAuth;
  }

  public openOfficeEdit(item: FileItem, route: ActivatedRoute) {
    const { params, queryParams, fragment } = route.snapshot;

    if (item.context === 'link') {
      const isLinkEditAuth = this.fileItemService.isOfficeLinkEdit(item, this.isUploadAllowed, this.isFileEditAllowed, this.isCwdShared);

      if (isLinkEditAuth) {
        const navigationParams = ['/4.0/dl', params['cachekey'], 'view', 'officefse', item.sync_id];

        if (params['key']) { 
          navigationParams.splice(2, 0, params['key']);
        }
        const extras = {
          ...this.LinkPathList.decorateQueryParams(queryParams),
          ...this.LinkPathList.decorateFragment(this.compat, fragment)
        }
        const url = this.router.createUrlTree(navigationParams,extras).toString();
        window.open(url, '_blank');
      }
    } else if (item.context === 'files') {
      if (this.user.can_preview) {
        const syncId = item.sync_id;
        const url = this.router.createUrlTree(['/4.0/file', syncId, 'view', 'officefse']).toString();
        window.open(url, '_blank');
      }
    }
  }

  public async openFile(item) {
    let url;
    if (item.context == 'applink') {
      url = item.compaturl;
    } else {
      url = await this.buildTransferItemService.getCompatUrl(
        item,
        this.buildTransferItemService.ACT_PREVIEW
      );
    }
    window.open(url, '_blank');
  }
  
  public async downloadCompat(data) {
    if (data.context == 'applink') {
      window.location.href = data.compaturl_dl;
    } else {
      const url = await this.buildTransferItemService.getCompatUrl(data, this.buildTransferItemService.ACT_DOWNLOAD)
      window.location.href = url;
    }
  }

  public showOfficeView(data) {
    return (
      this.fileItemService.isMSOffice(data, 'view') &&
      this.isAuth &&
      this.user.is_office &&
      this.user.is_pro
    );
  }

  public openOfficeView(item: FileItem) {
    const syncId = item.sync_id;
    const url = this.router.createUrlTree(['/4.0/file', syncId, 'view', 'officefsv']).toString();
    window.open(url, '_blank');
  }

  
  public showDownload(item) {
    if (
      (item.context == 'link' || item.context == 'applink') &&
      item.previewonly == 8
    ) {
      return false;
    } else {
      return true;
    }
  }

  public async duplicateFile(itemList: IFile[]) {
    const sync_id = itemList[0]['pid'];
    if (!sync_id) return;
    const selectedDirPathList: IFile[] = [];
    const duplicateNameMap: IDuplicateNameMap = {};
    await Promise.all(itemList.map((item: IFile) => this.moveActionsService.keepBoth(item, sync_id, itemList, selectedDirPathList, duplicateNameMap)));
    return await this.moveActionsService.startMovingOrCopyExecution(sync_id, itemList, WebpathActionType.COPY, duplicateNameMap);
  }

  public openFileInNewTab(link: string): void {
    this.router.navigate([]).then(res=>{
      window.open(link, '_blank');
    })
  }

  /**
     * Updates the starred (favorite) status of a list of file items.
     *
     * @param {FileItem[]} items - An array of `FileItem` objects to update.
     * @param {boolean} is_favorite - The new favorite status to set for the specified items.
     *                               If true, items will be marked as starred; 
     *                               if false, they will be unstarred.
    **/
  public async updateStarred(items: FileItem[], is_favorite: boolean) {
    if (is_favorite) {
      if (this.isFreeUser && this.total_favorite + items.length > 3) {
        this.dialogService.open(DialogUpgradeComponent);
        return;
      }
    }
    const originalItems = [...items];
    try {
        let decryptedName = '';
        if (items.length == 1) {
          decryptedName = await this.syncCryptService.filenameDecrypt(items[0].enc_name);
        }
        let starredMap = {};
        let syncIds = [];
        items.forEach(item => {
            starredMap[item.sync_id] = is_favorite;
            syncIds.push(item.sync_id);
        });
        const count = syncIds.length;
        this.store.dispatch(new FileListActions.UpdateStarred(starredMap));
        if (is_favorite) {
            // Adding items to starred
            await this.starredListService.addTag(syncIds, this.userService.get('uid'));
            this.blendService.track(BlendEvent.ADD_FAVORITE_TAG, { num_items: count });
            this.snackbarService.showMessage('success', count === 1
                ? `"${decryptedName}" added to starred`
                : `${count} items added to starred`);
        } else {
            // Removing items from starred
            await this.starredListService.removeTag(syncIds);
            this.blendService.track(BlendEvent.REMOVE_FAVORITE_TAG, { num_items: count });
            this.snackbarService.showMessage('success', count === 1
                ? `"${decryptedName}" removed from starred`
                : `${count} items removed from starred`);
        }
        
      }
      catch (error) {
        // reverting items to previous state
        let starredMap = {};
        originalItems.forEach(item => {
            starredMap[item.sync_id] = item.is_favorite;
        });
        this.store.dispatch(new FileListActions.UpdateStarred(starredMap));
        this.snackbarService.showMessage("critical", "Couldn't complete action. Try again.");
      }
  }
}
